import { createFileRoute, redirect } from '@tanstack/react-router';
import { queryClient } from '@/lib/queryClient';
import { onboardingKeys } from '@/lib/queryKeys';
import { fetchOnboardingStatus } from '@/hooks/onboarding/useOnboardingStatus';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Landing gate for `/dashboard`, resolved in priority order:
 *   1. Setup-capable users (`company:edit`) with unfinished onboarding land on
 *      the onboarding checklist — an owner who also happens to be linked to a
 *      Technician still finishes setup first.
 *   2. A linked field technician (no `company:edit`) lands on My Day, their own
 *      assigned schedule rather than the office operational dashboard.
 *   3. Everyone else goes straight to the dashboard home.
 *
 * Onboarding completion is derived live (no stored flag), so we force a fresh
 * fetch here — a stale "incomplete" from the 60s cache must never keep sending
 * a finished tenant back to onboarding.
 */
export const Route = createFileRoute('/_dashboard/dashboard/')({
  beforeLoad: async () => {
    const canSetUp = useAuthStore.getState().permissions.includes('company:edit');

    if (canSetUp) {
      let complete = true;
      try {
        const status = await queryClient.fetchQuery({
          queryKey: onboardingKeys.status(),
          queryFn: fetchOnboardingStatus,
          staleTime: 0,
        });
        complete = status.complete;
      } catch {
        // Never block the dashboard on a status hiccup — fail open.
        complete = true;
      }

      if (!complete) {
        throw redirect({ to: '/dashboard/onboarding' });
      }
    }

    // A linked *field* technician lands on their own day (assigned
    // appointments), not the office operational dashboard. Gate on `!canSetUp`
    // so a setup-capable owner/operator who also happens to be linked to a
    // Technician profile keeps the office home — only accounts without
    // `company:edit` are treated as field techs here. `technicianId` is the same
    // account↔Technician link the My Day route and its nav item gate on, so the
    // landing stays consistent with where a tech can actually go.
    if (!canSetUp && useAuthStore.getState().technicianId != null) {
      throw redirect({ to: '/dashboard/my-day' });
    }

    throw redirect({ to: '/dashboard/home' });
  },
  component: () => null,
});
